import { useState, useMemo } from 'react';

export type SupplierTagsMap = Record<string, string[]>; // supplierId -> tagIds[]

interface SupplierWithTags {
  id: string;
  tags?: Array<{ id: string; name: string }>;
}

export interface UseSupplierTagFilteringReturn {
  selectedTagIds: Set<string>;
  setSelectedTagIds: (
    ids: Set<string> | ((prev: Set<string>) => Set<string>),
  ) => void;
  supplierTagsMap: SupplierTagsMap;
  availableTagIds: Set<string>;
  matchesFilters: (supplierId: string) => boolean;
  filteredSupplierIds: Set<string>;
}

/**
 * Hook to manage tag filtering for suppliers
 * Uses OR logic: a supplier must have ANY selected tag to match
 *
 * Tags are provided directly from the forecast data (no lazy loading needed)
 */
export function useSupplierTagFiltering(
  suppliers: SupplierWithTags[] | undefined,
): UseSupplierTagFilteringReturn {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  // Build supplierTagsMap directly from suppliers array
  const supplierTagsMap = useMemo(() => {
    const tagsMap: SupplierTagsMap = {};
    if (!suppliers) return tagsMap;

    suppliers.forEach((supplier) => {
      tagsMap[supplier.id] = supplier.tags?.map((tag) => tag.id) || [];
    });

    return tagsMap;
  }, [suppliers]);

  // Collect all unique tag IDs available across all suppliers
  const availableTagIds = useMemo(() => {
    const tags = new Set<string>();
    Object.values(supplierTagsMap).forEach((supplierTags) => {
      supplierTags.forEach((tagId) => tags.add(tagId));
    });
    return tags;
  }, [supplierTagsMap]);

  // Helper: Check if a supplier matches the selected filters (OR logic)
  const matchesFilters = useMemo(() => {
    return (supplierId: string): boolean => {
      if (selectedTagIds.size === 0) {
        return true; // No filter = all suppliers match
      }

      const supplierTags = supplierTagsMap[supplierId] || [];
      // Check if supplier has ANY selected tag
      return Array.from(selectedTagIds).some((tagId) =>
        supplierTags.includes(tagId),
      );
    };
  }, [supplierTagsMap, selectedTagIds]);

  // Get all supplier IDs that match the current filters
  const filteredSupplierIds = useMemo(() => {
    if (!suppliers) return new Set<string>();

    if (selectedTagIds.size === 0) {
      // No tags selected = all suppliers match
      return new Set(suppliers.map((s) => s.id));
    }

    // Filter suppliers that have ANY selected tag
    const filtered = new Set<string>();
    suppliers.forEach((supplier) => {
      if (matchesFilters(supplier.id)) {
        filtered.add(supplier.id);
      }
    });

    return filtered;
  }, [suppliers, matchesFilters, selectedTagIds]);

  return {
    selectedTagIds,
    setSelectedTagIds,
    supplierTagsMap,
    availableTagIds,
    matchesFilters,
    filteredSupplierIds,
  };
}
